import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getLocalizedText, formatPrice } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Check, X, User, ChevronRight, Star, Search, Power, UserCog, Heart, GraduationCap, Users, BookOpen, Settings, AlertTriangle, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [data, setData] = useState({ 
    teachers: [], schools: [], services: [], courses: [], reviews: [], users: [], avatars: [],
    allServices: [], allCourses: []
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, endpoint: '', itemName: '', itemType: '' });

  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAuthenticated, user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [teachers, schools, services, courses, reviews, users, avatars, allServices, allCourses] = await Promise.all([
        axios.get(`${API_URL}/api/admin/pending-teachers`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-schools`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-services`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-courses`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-reviews`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-avatars`, { headers }),
        axios.get(`${API_URL}/api/admin/all-services`, { headers }),
        axios.get(`${API_URL}/api/admin/all-courses`, { headers }),
      ]);
      setData({
        teachers: teachers.data,
        schools: schools.data,
        services: services.data,
        courses: courses.data,
        reviews: reviews.data,
        users: users.data,
        avatars: avatars.data,
        allServices: allServices.data,
        allCourses: allCourses.data,
      });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleAction = async (endpoint, method = 'put', body = {}) => {
    try {
      if (method === 'put') {
        await axios.put(`${API_URL}${endpoint}`, body, { headers: getAuthHeaders() });
      } else if (method === 'delete') {
        await axios.delete(`${API_URL}${endpoint}`, { headers: getAuthHeaders() });
      }
      loadData();
    } catch (e) {
      alert('Error al procesar la accion');
    }
  };

  const handleDeleteWithConfirm = (endpoint, itemName, itemType = 'elemento') => {
    setDeleteDialog({ open: true, endpoint, itemName, itemType });
  };

  const confirmDelete = async () => {
    await handleAction(deleteDialog.endpoint, 'delete');
    setDeleteDialog({ open: false, endpoint: '', itemName: '', itemType: '' });
  };

  const handleDeleteUser = (userId, userName) => {
    setDeleteDialog({ 
      open: true, 
      endpoint: `/api/admin/users/${userId}`, 
      itemName: userName, 
      itemType: 'usuario (y todos sus servicios, cursos y datos asociados)' 
    });
  };

  const toggleFeatured = async (type, id, currentStatus) => {
    const endpoint = `/api/admin/${type}/${id}/feature?featured=${!currentStatus}`;
    await handleAction(endpoint, 'put');
  };

  const toggleUserActive = async (userId) => {
    await handleAction(`/api/admin/users/${userId}/toggle-active`, 'put');
  };

  const toggleServiceActive = async (serviceId) => {
    await handleAction(`/api/admin/services/${serviceId}/toggle-active`, 'put');
  };

  const toggleCourseActive = async (courseId) => {
    await handleAction(`/api/admin/courses/${courseId}/toggle-active`, 'put');
  };

  if (!isAuthenticated || !isAdmin) return null;

  const pendingCount = data.teachers.length + data.schools.length + data.services.length + data.courses.length + data.reviews.length + data.avatars.length;
  
  // Filter users by search query
  const filteredUsers = data.users.filter(u => 
    !searchQuery || 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort services and courses: featured first, then by date
  const sortedServices = [...data.allServices].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    return 0;
  });

  const sortedCourses = [...data.allCourses].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    return 0;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 animate-fade-in" data-testid="admin-page">
      <h1 className="font-serif text-4xl font-bold text-primary mb-8">{t('admin.dashboard')}</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="pending" data-testid="tab-pending" className="gap-2">
            <Settings className="h-4 w-4" />
            Pendientes {pendingCount > 0 && <Badge className="ml-1 bg-red-500">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="gap-2">
            <Users className="h-4 w-4" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content" className="gap-2">
            <Star className="h-4 w-4" />
            Contenidos
          </TabsTrigger>
        </TabsList>

        {/* PENDING TAB */}
        <TabsContent value="pending">
          {loading ? (
            <p className="text-center py-8">{t('common.loading')}</p>
          ) : (
            <div className="space-y-8">
              {/* Pending Teachers */}
              {data.teachers.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-purple-500" />
                    Profesionales Pendientes ({data.teachers.length})
                  </h3>
                  <div className="space-y-3">
                    {data.teachers.map((teacher) => (
                      <div key={teacher.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                        <div>
                          <p className="font-medium">{teacher.user_name}</p>
                          <p className="text-sm text-secondary">{teacher.user_email}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAction(`/api/admin/teachers/${teacher.id}/approve`)} className="bg-green-500 text-white">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleAction(`/api/admin/teachers/${teacher.id}`, 'delete')}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Schools */}
              {data.schools.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-500" />
                    Escuelas Pendientes ({data.schools.length})
                  </h3>
                  <div className="space-y-3">
                    {data.schools.map((school) => (
                      <div key={school.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                        <div>
                          <p className="font-medium">{getLocalizedText(school.name, 'es')}</p>
                          <p className="text-sm text-secondary">{school.user_email}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAction(`/api/admin/schools/${school.id}/approve`)} className="bg-green-500 text-white">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleAction(`/api/admin/schools/${school.id}`, 'delete')}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Services */}
              {data.services.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Heart className="h-5 w-5 text-pink-500" />
                    Servicios Pendientes ({data.services.length})
                  </h3>
                  <div className="space-y-3">
                    {data.services.map((service) => (
                      <div key={service.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                        <div>
                          <p className="font-medium">{getLocalizedText(service.title, 'es')}</p>
                          <p className="text-sm text-secondary">{service.teacher_name} • {formatPrice(service.price)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAction(`/api/admin/services/${service.id}/approve`)} className="bg-green-500 text-white">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleAction(`/api/admin/services/${service.id}`, 'delete')}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Courses */}
              {data.courses.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-orange-500" />
                    Cursos Pendientes ({data.courses.length})
                  </h3>
                  <div className="space-y-3">
                    {data.courses.map((course) => (
                      <div key={course.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                        <div>
                          <p className="font-medium">{getLocalizedText(course.title, 'es')}</p>
                          <p className="text-sm text-secondary">
                            {course.school_name ? getLocalizedText(course.school_name, 'es') : course.teacher_name} • {formatPrice(course.price)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAction(`/api/admin/courses/${course.id}/approve`)} className="bg-green-500 text-white">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleAction(`/api/admin/courses/${course.id}`, 'delete')}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Reviews */}
              {data.reviews.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4">Reseñas Pendientes ({data.reviews.length})</h3>
                  <div className="space-y-3">
                    {data.reviews.map((review) => (
                      <div key={review.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{review.rating}/5 estrellas</span>
                            <span className="text-sm text-secondary">para {review.target_name}</span>
                          </div>
                          <p className="text-sm mt-1">{review.comment}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAction(`/api/admin/reviews/${review.id}/approve`)} className="bg-green-500 text-white">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleAction(`/api/admin/reviews/${review.id}`, 'delete')}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Avatars */}
              {data.avatars.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4">Fotos de Perfil Pendientes ({data.avatars.length})</h3>
                  <div className="space-y-3">
                    {data.avatars.map((item) => (
                      <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="flex gap-3 items-center">
                            <div className="text-center">
                              <p className="text-xs text-secondary mb-1">Actual</p>
                              <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100">
                                {item.current_avatar ? (
                                  <img src={item.current_avatar} alt="Current" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <User className="w-8 h-8 text-gray-400" />
                                  </div>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-gray-400" />
                            <div className="text-center">
                              <p className="text-xs text-secondary mb-1">Nueva</p>
                              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-yellow-400">
                                {item.pending_avatar && (
                                  <img src={item.pending_avatar} alt="Pending" className="w-full h-full object-cover" />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 ml-4">
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-secondary">{item.email}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleAction(`/api/admin/avatars/${item.id}/approve`)} className="bg-green-500 text-white">
                              <Check className="h-4 w-4 mr-1" /> Aprobar
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleAction(`/api/admin/avatars/${item.id}/reject`)}>
                              <X className="h-4 w-4 mr-1" /> Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pendingCount === 0 && (
                <div className="text-center py-12 bg-white rounded-lg">
                  <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-secondary">No hay elementos pendientes de aprobación</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users">
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Buscar por nombre o email..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full max-w-md"
              />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Usuario</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Roles</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Estado</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className={!u.is_active && u.is_active !== undefined ? 'bg-red-50' : ''}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-secondary">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.roles?.map((role, idx) => (
                            <Badge key={idx} variant="outline" className={
                              role === 'admin' ? 'border-red-500 text-red-500' :
                              role === 'teacher' ? 'border-purple-500 text-purple-500' :
                              role === 'school' ? 'border-blue-500 text-blue-500' :
                              ''
                            }>
                              {role === 'teacher' ? 'Profesional' : role === 'school' ? 'Escuela' : role}
                            </Badge>
                          )) || <Badge variant="outline">{u.role}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {u.is_active === false ? (
                          <Badge className="bg-red-500 text-white">Deshabilitado</Badge>
                        ) : (
                          <Badge className="bg-green-500 text-white">Activo</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {u.role !== 'admin' && !u.roles?.includes('admin') && (
                            <>
                              <Button 
                                size="sm" 
                                variant={u.is_active === false ? "default" : "outline"}
                                onClick={() => toggleUserActive(u.id)}
                                className={u.is_active === false ? "bg-green-500 text-white" : ""}
                                title={u.is_active === false ? 'Habilitar usuario' : 'Deshabilitar usuario'}
                              >
                                <Power className="h-4 w-4 mr-1" />
                                {u.is_active === false ? 'Habilitar' : 'Deshabilitar'}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteUser(u.id, u.name)}
                                title="Eliminar usuario definitivamente"
                              >
                                <X className="h-4 w-4" /> Eliminar
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* CONTENT TAB */}
        <TabsContent value="content">
          <div className="space-y-8">
            {/* Services Management */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-500" />
                Gestión de Servicios ({data.allServices.length})
              </h3>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Servicio</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Profesional</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Precio</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Estado</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedServices.map((service) => (
                        <tr key={service.id} className={!service.is_active ? 'bg-gray-50 opacity-60' : ''}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {service.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                              <span className="font-medium">{getLocalizedText(service.title, 'es')}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-secondary">{service.teacher_name}</td>
                          <td className="px-4 py-3">{formatPrice(service.price)}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {service.is_approved ? (
                                <Badge className="bg-green-500 text-white">Aprobado</Badge>
                              ) : (
                                <Badge className="bg-yellow-500 text-white">Pendiente</Badge>
                              )}
                              {!service.is_active && (
                                <Badge className="bg-gray-500 text-white">Deshabilitado</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant={service.is_featured ? "default" : "outline"}
                                onClick={() => toggleFeatured('services', service.id, service.is_featured)}
                                className={service.is_featured ? "bg-yellow-500 text-white" : ""}
                                title={service.is_featured ? 'Quitar de destacados' : 'Destacar servicio'}
                              >
                                <Star className={`h-4 w-4 ${service.is_featured ? 'fill-white' : ''}`} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant={service.is_active ? "outline" : "default"}
                                onClick={() => toggleServiceActive(service.id)}
                                className={!service.is_active ? "bg-green-500 text-white" : ""}
                                title={service.is_active ? 'Deshabilitar servicio' : 'Habilitar servicio'}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteWithConfirm(`/api/admin/services/${service.id}`, getLocalizedText(service.title, 'es'), 'servicio')}
                                title="Eliminar servicio definitivamente"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Courses Management */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-orange-500" />
                Gestión de Cursos ({data.allCourses.length})
              </h3>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Curso</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Organizador</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Precio</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Estado</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-secondary">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedCourses.map((course) => (
                        <tr key={course.id} className={!course.is_active ? 'bg-gray-50 opacity-60' : ''}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {course.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                              <span className="font-medium">{getLocalizedText(course.title, 'es')}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-secondary">
                            {course.school_name ? getLocalizedText(course.school_name, 'es') : course.teacher_name}
                          </td>
                          <td className="px-4 py-3">{formatPrice(course.price)}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {course.is_approved ? (
                                <Badge className="bg-green-500 text-white">Aprobado</Badge>
                              ) : (
                                <Badge className="bg-yellow-500 text-white">Pendiente</Badge>
                              )}
                              {!course.is_active && (
                                <Badge className="bg-gray-500 text-white">Deshabilitado</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant={course.is_featured ? "default" : "outline"}
                                onClick={() => toggleFeatured('courses', course.id, course.is_featured)}
                                className={course.is_featured ? "bg-yellow-500 text-white" : ""}
                                title={course.is_featured ? 'Quitar de destacados' : 'Destacar curso'}
                              >
                                <Star className={`h-4 w-4 ${course.is_featured ? 'fill-white' : ''}`} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant={course.is_active ? "outline" : "default"}
                                onClick={() => toggleCourseActive(course.id)}
                                className={!course.is_active ? "bg-green-500 text-white" : ""}
                                title={course.is_active ? 'Deshabilitar curso' : 'Habilitar curso'}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteWithConfirm(`/api/admin/courses/${course.id}`, getLocalizedText(course.title, 'es'), 'curso')}
                                title="Eliminar curso definitivamente"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogContent className="sm:max-w-md bg-white border-2 border-gray-200 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription className="text-left pt-4 text-gray-700">
              ¿Estás seguro de que quieres eliminar el {deleteDialog.itemType} <strong className="text-gray-900">"{deleteDialog.itemName}"</strong> definitivamente?
              <br /><br />
              <span className="text-red-600 font-semibold">⚠️ Esta acción no se puede deshacer.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-3 pt-4">
            <Button variant="outline" onClick={() => setDeleteDialog({ ...deleteDialog, open: false })} className="flex-1">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete} className="gap-2 flex-1">
              <Trash2 className="h-4 w-4" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
